import { useState, useEffect, useRef, useCallback } from "react";
import { Check, X, Edit3, RefreshCw, Save, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { difficultyColor } from "./types";
import { ProblemText } from "./ProblemText";
import { problemTextToPreview } from "./problem-text";
import { ProblemImages } from "./ProblemImages";

interface LiveProblem {
  id: string;
  skill_name: string;
  standard_code: string;
  grade: string;
  question_text: string;
  question_type: string;
  difficulty_level: string;
  answer_choices: string[] | null;
  correct_answer: string | null;
  images: string[] | null;
  status: string;
  eval_result: {
    math?: boolean;
    standard?: boolean;
    difficulty?: boolean;
    verdict?: string;
  } | null;
  promoted_problem_id: string | null;
  reviewer_notes: string | null;
}

interface EditDraft {
  question_text: string;
  answer_choices: string[];
  correct_answer: string;
  difficulty_level: string;
}

function EvalBadge({ ok, label }: { ok: boolean | undefined; label: string }) {
  if (ok === undefined) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold ${
        ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
      }`}
    >
      {ok ? <Check className="h-2.5 w-2.5" /> : <X className="h-2.5 w-2.5" />} {label}
    </span>
  );
}

const STATUS_TO_DOT: Record<string, string> = {
  approved: "bg-emerald-500",
  raw: "bg-amber-400",
  rejected: "bg-red-400",
  promoted: "bg-blue-500",
};

type FilterKey = "all" | "pass" | "review" | "fail";

const FILTERS: [FilterKey, string][] = [
  ["all", "All"],
  ["pass", "Passed"],
  ["review", "Review"],
  ["fail", "Failed"],
];

export function ReviewUI() {
  const [revFilter, setRevFilter] = useState<FilterKey>("all");
  const [allProblems, setAllProblems] = useState<LiveProblem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selProb, setSelProb] = useState<LiveProblem | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;
  const fetchSeq = useRef(0);

  const fetchProblems = useCallback(async (offset = 0, filter: FilterKey = revFilter) => {
    const seq = ++fetchSeq.current;
    setLoading(true);
    setFetchError(null);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset), exclude_promoted: "true" });
      if (filter !== "all") params.set("verdict", filter);
      const url = `/api/proxy/question-bank/generated-problems?${params}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (seq !== fetchSeq.current) return;
      setAllProblems(data.items ?? []);
      setTotal(data.total ?? 0);
    } catch (e) {
      if (seq !== fetchSeq.current) return;
      setFetchError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, [revFilter]);

  useEffect(() => {
    fetchProblems(page * PAGE_SIZE, revFilter);
  }, [fetchProblems, page, revFilter]);

  const problems = allProblems;

  function selectProblem(p: LiveProblem) {
    setSelProb(p);
    setEditing(false);
    setDraft(null);
    setSaveError(null);
  }

  function startEdit() {
    if (!selProb) return;
    setDraft({
      question_text: selProb.question_text,
      answer_choices: selProb.answer_choices ? [...selProb.answer_choices] : [],
      correct_answer: selProb.correct_answer ?? "",
      difficulty_level: selProb.difficulty_level,
    });
    setEditing(true);
    setSaveError(null);
  }

  function cancelEdit() {
    setEditing(false);
    setDraft(null);
    setSaveError(null);
  }

  async function saveEdit() {
    if (!selProb || !draft) return;
    setActionLoading("save");
    setSaveError(null);
    try {
      const res = await fetch(`/api/proxy/question-bank/generated-problems/${selProb.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question_text: draft.question_text,
          answer_choices: draft.answer_choices.length > 0 ? draft.answer_choices : null,
          correct_answer: draft.correct_answer || null,
          difficulty_level: draft.difficulty_level,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Save failed" }));
        setSaveError(err.detail ?? "Save failed");
        return;
      }
      const updated: LiveProblem = await res.json();
      setAllProblems((prev) => prev.map((p) => p.id === updated.id ? updated : p));
      setSelProb(updated);
      setEditing(false);
      setDraft(null);
    } finally {
      setActionLoading(null);
    }
  }

  async function patchStatus(id: string, status: "approved" | "rejected") {
    setActionLoading(id + status);
    try {
      await fetch(`/api/proxy/question-bank/generated-problems/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      setAllProblems((prev) => prev.map((p) => p.id === id ? { ...p, status } : p));
      if (selProb?.id === id) setSelProb((p) => p ? { ...p, status } : p);
    } finally {
      setActionLoading(null);
    }
  }

  async function promote(id: string) {
    setActionLoading(id + "promote");
    try {
      const res = await fetch(`/api/proxy/question-bank/generated-problems/${id}/promote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (res.ok) {
        const data = await res.json();
        setAllProblems((prev) =>
          prev.map((p) =>
            p.id === id ? { ...p, status: "promoted", promoted_problem_id: data.id } : p
          )
        );
        if (selProb?.id === id)
          setSelProb((p) => p ? { ...p, status: "promoted", promoted_problem_id: data.id } : p);
      }
    } finally {
      setActionLoading(null);
    }
  }

  return (
    <div className="min-w-0 space-y-5 overflow-hidden">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold">Review UI</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Approve, edit, or reject generated problems
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => fetchProblems(page * PAGE_SIZE, revFilter)}>
          <RefreshCw className="mr-1 h-3.5 w-3.5" /> Refresh
        </Button>
      </div>

      {/* Filter Pills */}
      <div className="flex gap-1.5">
        {FILTERS.map(([key, label]) => (
          <Button
            key={key}
            variant={revFilter === key ? "default" : "outline"}
            size="sm"
            onClick={() => { setRevFilter(key); setPage(0); }}
            className="text-xs"
          >
            {label}
          </Button>
        ))}
        {!loading && (
          <span className="ml-2 self-center text-xs text-muted-foreground">
            {total} problems
          </span>
        )}
      </div>

      {/* Two Column */}
      <div className="grid min-w-0 grid-cols-2 gap-4 overflow-hidden" style={{ height: "calc(100vh - 280px)" }}>
        {/* Problem List */}
        <Card className="flex min-w-0 flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="space-y-0">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="border-b px-4 py-3 last:border-b-0">
                    <Skeleton className="mb-1.5 h-3 w-24" />
                    <Skeleton className="h-3 w-full" />
                  </div>
                ))}
              </div>
            ) : fetchError ? (
              <div className="px-4 py-8 text-center text-sm text-red-500">
                {/* onClick={fetchProblems} passed the MouseEvent as `offset`,
                    producing offset=[object Object] — retry always failed. */}
                {fetchError} —{" "}
                <button
                  className="cursor-pointer border-none bg-transparent p-0 underline"
                  onClick={() => fetchProblems(page * PAGE_SIZE, revFilter)}
                >
                  retry
                </button>
              </div>
            ) : problems.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No problems match
              </div>
            ) : (
              problems.map((p) => (
                <button
                  key={p.id}
                  onClick={() => selectProblem(p)}
                  className={`w-full cursor-pointer border-b bg-transparent px-4 py-3 text-left transition-colors last:border-b-0 ${
                    selProb?.id === p.id ? "bg-muted" : "hover:bg-muted/50"
                  }`}
                >
                  <div className="mb-1 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-block h-2 w-2 rounded-full ${STATUS_TO_DOT[p.status] ?? "bg-neutral-300"}`}
                      />
                      <span className="text-sm font-semibold">{p.standard_code || p.skill_name || "No standard"}</span>
                    </div>
                    <Badge variant={difficultyColor(p.difficulty_level)} className="text-[10px]">
                      {p.difficulty_level}
                    </Badge>
                  </div>
                  <p className="truncate text-xs text-muted-foreground">
                    {problemTextToPreview(p.question_text)}
                  </p>
                </button>
              ))
            )}
          </div>
          {/* Pagination */}
          <div className="flex items-center justify-between border-t px-3 py-2">
            <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
              Prev
            </Button>
            <span className="text-xs text-muted-foreground">
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            </span>
            <Button size="sm" variant="outline" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        </Card>

        {/* Detail Panel */}
        <Card className="flex min-h-0 min-w-0 flex-col overflow-hidden">
          {selProb ? (
            <>
              {/* Scrollable content */}
              {/* overflow-x-auto, not hidden: this pane is half the viewport and
                  scraped figures are routinely wider, so hiding the overflow
                  cropped them with no way to reach the rest. */}
              <div className="min-w-0 flex-1 space-y-3 overflow-auto p-4">
                <div className="flex items-center justify-between">
                  <span className="text-base font-bold">{selProb.standard_code || selProb.skill_name || "No standard"}</span>
                  <div className="flex gap-1.5">
                    <Badge variant="outline">{selProb.skill_name}</Badge>
                    <Badge
                      variant="outline"
                      className={`capitalize ${
                        selProb.status === "promoted" ? "border-blue-400 text-blue-600" :
                        selProb.status === "rejected" ? "border-red-400 text-red-600" : ""
                      }`}
                    >
                      {selProb.status}
                    </Badge>
                  </div>
                </div>

                {editing && draft ? (
                  /* ── Edit mode ── */
                  <div className="space-y-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-muted-foreground">Question</label>
                      <textarea
                        rows={4}
                        value={draft.question_text}
                        onChange={(e) => setDraft({ ...draft, question_text: e.target.value })}
                        className="w-full resize-y rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>

                    {draft.answer_choices.length > 0 ? (
                      <div>
                        <label className="mb-1 block text-xs font-medium text-muted-foreground">Answer Choices</label>
                        <div className="space-y-1.5">
                          {draft.answer_choices.map((choice, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <input
                                type="radio"
                                name="correct"
                                checked={draft.correct_answer === choice}
                                onChange={() => setDraft({ ...draft, correct_answer: choice })}
                                className="shrink-0"
                              />
                              <input
                                value={choice}
                                onChange={(e) => {
                                  const next = [...draft.answer_choices];
                                  const wasCorrect = draft.correct_answer === choice;
                                  next[i] = e.target.value;
                                  setDraft({
                                    ...draft,
                                    answer_choices: next,
                                    correct_answer: wasCorrect ? e.target.value : draft.correct_answer,
                                  });
                                }}
                                className="flex-1 rounded-lg border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
                              />
                            </div>
                          ))}
                        </div>
                        <p className="mt-1 text-[10px] text-muted-foreground">Select radio to mark correct answer</p>
                      </div>
                    ) : (
                      <div>
                        <label className="mb-1 block text-xs font-medium text-muted-foreground">Correct Answer</label>
                        <input
                          value={draft.correct_answer}
                          onChange={(e) => setDraft({ ...draft, correct_answer: e.target.value })}
                          className="w-full rounded-lg border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
                        />
                      </div>
                    )}

                    <div>
                      <label className="mb-1 block text-xs font-medium text-muted-foreground">Difficulty</label>
                      <select
                        value={draft.difficulty_level}
                        onChange={(e) => setDraft({ ...draft, difficulty_level: e.target.value })}
                        className="rounded-lg border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
                      >
                        <option value="easy">Easy</option>
                        <option value="medium">Medium</option>
                        <option value="hard">Hard</option>
                      </select>
                    </div>

                    {saveError && (
                      <p className="text-xs text-red-600">{saveError}</p>
                    )}
                  </div>
                ) : (
                  /* ── View mode ── */
                  <>
                    <div className="qb-safe rounded-lg bg-muted/30 p-3 text-sm leading-relaxed break-words">
                      <ProblemText text={selProb.question_text} />
                    </div>
                    <ProblemImages images={selProb.images} />
                    {selProb.answer_choices && Array.isArray(selProb.answer_choices) && (
                      <div className="qb-safe flex flex-wrap gap-1.5">
                        {selProb.answer_choices.map((o, i) => (
                          <span
                            key={i}
                            className={`qb-choice qb-choice-sm rounded-lg px-3 py-1 text-xs font-medium break-words ${
                              o === selProb.correct_answer
                                ? "bg-primary text-primary-foreground"
                                : "border bg-background"
                            }`}
                          >
                            <ProblemText text={o} inline />
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Matches the pills above when there are pills to match —
                        otherwise a figure answer rendered at 120px beside the
                        same figure at 40px in the list. See ExtractionEngine. */}
                    {selProb.correct_answer && (
                      <div
                        className={`qb-safe ${
                          Array.isArray(selProb.answer_choices) && selProb.answer_choices.length
                            ? "qb-choice qb-choice-sm"
                            : "qb-answer"
                        } text-sm font-semibold`}
                      >
                        Answer: <ProblemText text={selProb.correct_answer} inline />
                      </div>
                    )}
                    {selProb.eval_result && (
                      <div className="flex gap-1.5">
                        <EvalBadge ok={selProb.eval_result.math} label="Math" />
                        <EvalBadge ok={selProb.eval_result.standard} label="Std" />
                        <EvalBadge ok={selProb.eval_result.difficulty} label="Diff" />
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Sticky action bar */}
              <div className="flex shrink-0 flex-wrap gap-2 border-t p-4">
                {editing ? (
                  <>
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      disabled={!!actionLoading}
                      onClick={saveEdit}
                    >
                      <Save className="mr-1 h-3.5 w-3.5" /> Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={cancelEdit}>
                      <XCircle className="mr-1 h-3.5 w-3.5" /> Cancel
                    </Button>
                  </>
                ) : selProb.status !== "promoted" ? (
                  <>
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      disabled={!!actionLoading}
                      onClick={() => promote(selProb.id)}
                    >
                      <Check className="mr-1 h-3.5 w-3.5" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={startEdit} disabled={!!actionLoading}>
                      <Edit3 className="mr-1 h-3.5 w-3.5" /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={selProb.status === "rejected" || !!actionLoading}
                      onClick={() => patchStatus(selProb.id, "rejected")}
                    >
                      <X className="mr-1 h-3.5 w-3.5" /> Reject
                    </Button>
                  </>
                ) : (
                  <span className="text-sm font-medium text-emerald-600">
                    ✓ Added to question bank
                  </span>
                )}
              </div>
            </>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Select a problem to review
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
